<template>
    <v-card
        class="fill-height"
        :class="{ 'in-modal': inModal }"
        flat
    >
        <Dimmer
            :hide-spinner="false"
            :model-value="progress"
        >
            <template #content>
                <component
                    :is="cmpType"
                    :class="{ 'd-flex flex-column h-100': inModal }"
                    @submit="onSubmit($event)"
                >
                    <v-card-title
                        v-if="!hideToolbar"
                        class="d-block pa-0"
                    >
                        <v-toolbar
                            color="secondary"
                            density="compact"
                            flat
                        >
                            <v-toolbar-title
                                class="text-h6"
                            >
                                {{ title }}
                            </v-toolbar-title>
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="inModal"
                                @click="onExit()"
                                :icon="mdiWindowClose"
                            >
                            </v-btn>
                        </v-toolbar>
                    </v-card-title>

                    <v-card-text
                        class="pb-0"
                        :class="{
                            'my-6 px-4': !hideToolbar
                        }"
                    >
                        <!-- First / Last Name -->
                        <v-row dense>
                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="firstName"
                                    v-bind="firstNameProps"
                                    name="firstName"
                                    label="First Name"
                                    variant="outlined"
                                    autocorrect="off"
                                    autocomplete="given-name"
                                ></v-text-field>
                            </v-col>
                            <v-col
                                cols="12"
                                sm="6"
                            >
                                <v-text-field
                                    v-model="lastName"
                                    v-bind="lastNameProps"
                                    label="Last Name"
                                    name="lastName"
                                    autocorrect="off"
                                    autocomplete="family-name"
                                    variant="outlined"
                                    required
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Email -->
                        <v-row dense>
                            <v-col>
                                <v-text-field
                                    v-model="email"
                                    v-bind="emailProps"
                                    label="Email"
                                    autocorrect="off"
                                    autocapitalize="none"
                                    autocomplete="email"
                                    variant="outlined"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Phone -->
                        <v-row dense>
                            <v-col>
                                <v-text-field
                                    v-model="phone"
                                    v-bind="phoneProps"
                                    name="phone"
                                    label="Phone"
                                    autocomplete="tel"
                                    autocorrect="off"
                                    variant="outlined"
                                ></v-text-field>
                            </v-col>
                        </v-row>

                        <!-- Gender -->
                        <v-row
                            class="mb-6"
                            dense
                        >
                            <v-col>
                                <label
                                    class="d-block font-weight-medium"
                                >
                                    Gender (optional)
                                </label>
                                <GenderRadioSelectorInput
                                    v-model="gender"
                                    v-bind="genderProps"
                                    name="gender"
                                ></GenderRadioSelectorInput>
                            </v-col>
                        </v-row>

                        <!-- Role -->
                        <v-row dense>
                            <v-col>
                                <v-autocomplete
                                    v-model="role"
                                    v-bind="roleProps"
                                    :items="roles"
                                    item-title="name"
                                    item-value="uid"
                                    name="role"
                                    label="Role"
                                    hide-details="auto"
                                ></v-autocomplete>
                            </v-col>
                        </v-row>
                    </v-card-text>

                    <v-card-actions
                        v-if="!hideActions"
                    >
                        <v-spacer></v-spacer>
                        <v-btn
                            v-if="inModal"
                            variant="text"
                            color="secondary"
                            size="large"
                            @click="onExit()"
                        >
                            Cancel
                        </v-btn>
                        <v-btn
                            variant="flat"
                            class="mr-4"
                            color="primary"
                            type="submit"
                            :disabled="progress || !formMeta.valid"
                            size="large"
                        >
                            {{ isInEditMode ? 'Update' : 'Add' }}
                        </v-btn>
                    </v-card-actions>
                </component>
            </template>
        </Dimmer>
    </v-card>
</template>

<script lang="ts" setup>
    import { toTypedSchema } from '@vee-validate/valibot';

    import {
        OrgTeamSignupContactModel as contactModel
    } from '~/models';
    import type { OrgSignUpContact, MembershipRole } from '~/types';
    import { useFormValidation } from 'x1-base/composables';
    import Dimmer from 'x1-base/components/Dimmer/Dimmer.vue';
    import GenderRadioSelectorInput from '~/components/controls/GenderRadioSelectorInput/GenderRadioSelectorInput.vue';
    import { isEmpty as lo_isEmpty } from 'es-toolkit/compat';
    import { mdiWindowClose } from '@mdi/js';
    import type { WatchStopHandle } from 'vue';


    const props = defineProps({
        modelValue: {
            type: Object as PropType<Nilish<OrgSignUpContact>>,
            default: () => {}
        },
        inModal: {
            type: Boolean,
            default: false
        },
        hideToolbar: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        hideActions: {
            type: Boolean as PropType<boolean>,
            default: false
        },
        title: {
            type: String,
            default: 'Contact'
        },
        progress: {
            type: Boolean,
            default: false
        },
        roles: {
            type: Array as PropType<MembershipRole[]>,
            default: () => []
        },
        isNestedForm: {
            type: Boolean as PropType<boolean>,
            default: false
        }
    });

    const emit = defineEmits<{
        (e: 'update:modelValue', value: OrgSignUpContact): void;
        (e: 'on-save', value: OrgSignUpContact): void;
        (e: 'on-exit'): void;
    }>();

    let unWatchIsValid: WatchStopHandle;
    const cmpType = ref<Nullable<string>>(props.isNestedForm ? 'div' : 'form');
    const { getDefineFieldOptions } = useFormValidation();

    const {
        handleSubmit,
        defineField,
        values: formValues,
        controlledValues: controlledFormValues,
        meta: formMeta,
        validate: validateForm
    } = useForm<OrgSignUpContact>({
        validationSchema: toTypedSchema(
            contactModel
        )
    });

    const vo = () => getDefineFieldOptions('vuetify');
    const [firstName, firstNameProps] = defineField('firstName', vo());
    const [lastName, lastNameProps] = defineField('lastName', vo());
    const [email, emailProps] = defineField('email', vo());
    const [phone, phoneProps] = defineField('phone', vo());
    const [gender, genderProps] = defineField('gender', vo());
    const [role, roleProps] = defineField('role', vo());

    const onSubmit = handleSubmit((values: OrgSignUpContact, ctx) => {
        emit('update:modelValue', values);
        emit('on-save', values);
    });

    const isInEditMode = computed(() => !lo_isEmpty(props.modelValue));

    if (props.hideActions) {
        unWatchIsValid = watch(
            () => formMeta.value.valid,
            (isValid) => {
                const obj = controlledFormValues.value;
                obj.gender = obj.gender ?? null;
                const value = isValid ? obj : null;
                // @ts-ignore
                emit('update:modelValue', value);
            }
        );
    }

    function onExit(): void {
        emit('on-exit');
    }

    onBeforeUnmount(() => {
        unWatchIsValid?.();
    });


    defineExpose({ validateForm });
</script>

<style lang="scss" scoped></style>
